import { BooleanInput, DateRange, InputSelectSync, Search } from '@chipinside/frontend'
import { ResponseBooleanFilter } from '@chipinside/frontend/dist/Inputs/Boolean/types'
import { ResponseDateRangeFilter } from '@chipinside/frontend/dist/Inputs/DateRange/types'
import {
  InputSelectOption,
  InputSelectValue,
  ResponseSelectFilter,
} from '@chipinside/frontend/dist/Inputs/Select/types'
import { ElementBlock, PageBuilderElementBase } from '@chipinside/pagebuilder/dist/types'
import { Translate } from '@chipinside/translate'
import { Box, useMediaQuery } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { debounce, isEmpty, omit, uniqueId } from 'lodash'

import { PageBuilderAction } from '#/store/providers/Pagebuilder/Components/Action'
import OptionGroup from '#/store/providers/Pagebuilder/Components/Filters/OptionGroup'

const StyledBox = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
})

export type PageBuilderFiltersProps = Pick<ElementBlock, 'filters' | 'action'> &
  PageBuilderElementBase

export function PageBuilderFilters(props: PageBuilderFiltersProps) {
  const { action, filterProps = {}, filters = [], onChangeFilters = () => {}, ...rest } = props
  if (isEmpty(filters)) return null

  const { children } = filterProps

  const formattedFilters = {
    boolean: filters.filter(({ type, visible }) => type === 'boolean' && visible),
    dateRange: filters.filter(({ type, visible }) => type === 'date-range' && visible),
    option_group: filters.filter(
      ({ type, visible }) => type === ('option-group' as any) && visible,
    ),
    select: filters.filter(({ type, visible }) => type === 'select' && visible),
  }

  const searchFilter = filters.find(
    ({ type, visible }) => type === ('search' as any) && visible,
  ) as any

  const theme = useTheme()

  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'))

  const dateRange = formattedFilters.dateRange.map(item => {
    const filter = item as ResponseDateRangeFilter

    return (
      <DateRange
        {...filter}
        key={filter.name}
        defaultValues={filter.current}
        title={filter.title}
        containerStyle={{
          minWidth: 200,
        }}
        onChange={value => {
          onChangeFilters?.(prevState => ({
            ...prevState,
            [filter.name]: value,
          }))
        }}
      />
    )
  })
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: { sm: 'space-between', xs: 'center' },
        padding: '8px 8px 16px',
      }}
    >
      <StyledBox
        id="pagebuilder-filters"
        sx={{
          '& > div': { [theme.breakpoints.only('xs')]: { width: '100%' }, width: 200 },
          [theme.breakpoints.only('xs')]: { width: '100%' },
        }}
      >
        {searchFilter && (
          <Search
            value={searchFilter?.current}
            placeholder={searchFilter?.placeholder}
            onChange={debounce(value => {
              onChangeFilters?.(prevState => ({
                ...prevState,
                [searchFilter.name]: value,
              }))
            }, 1000)}
            autoFocus
          />
        )}
        {formattedFilters.select.map(item => {
          const filter = item as ResponseSelectFilter

          // TODO: padronizar para multi no backend e front, todos os locais
          const multi = filter.isMulti || filter.multi
          return (
            <InputSelectSync
              {...filter}
              placeholder={
                filter?.placeholder ??
                Translate({
                  messageKey: 'select_one_item_female',
                  params: {
                    item: Translate({ lower: true, messageKey: 'option' }),
                  },
                })
              }
              key={filter.name}
              multi={multi}
              label={filter.title}
              limitTags={2}
              value={filter.current}
              onChange={(options, name) => {
                if (isEmpty(options)) {
                  return onChangeFilters?.(prevState => omit(prevState, name))
                }

                if (multi) {
                  const value = (options as InputSelectOption[])?.map(
                    ({ value }) => value as InputSelectValue,
                  )
                  return onChangeFilters?.(prevState => ({
                    ...prevState,
                    [name]: value,
                  }))
                }

                return onChangeFilters?.(prevState => {
                  return {
                    ...prevState,
                    [name]: (options as InputSelectOption)?.value,
                  }
                })
              }}
            />
          )
        })}
        {formattedFilters.boolean.map(item => {
          const filter = item as ResponseBooleanFilter

          return (
            <BooleanInput
              {...filter}
              key={uniqueId(filter.name)}
              label={filter.title}
              value={filter.current}
              onChange={(value, name) => {
                onChangeFilters?.(prevState => ({
                  ...prevState,
                  [name]: value,
                }))
              }}
            />
          )
        })}
        {children}
        {isSmallScreen && dateRange}
      </StyledBox>
      <StyledBox
        sx={{ [theme.breakpoints.only('xs')]: { flexDirection: 'column', width: '100%' } }}
      >
        {!isEmpty(formattedFilters.option_group) && (
          <Box sx={{ gap: 2 }}>
            {formattedFilters.option_group.map(item => {
              const filter = item as any

              return (
                <OptionGroup
                  {...filter}
                  key={filter.name}
                  id={filter.name}
                  onChange={value => {
                    onChangeFilters?.(prevState => ({
                      ...prevState,
                      [filter.name]: value,
                    }))
                  }}
                />
              )
            })}
          </Box>
        )}
        {!isEmpty(action) && (
          <Box>
            <PageBuilderAction {...(rest as any)} {...action} />
          </Box>
        )}
        {!isSmallScreen && dateRange}
      </StyledBox>
    </Box>
  )
}
